<template>
    <div>
        <page-header
            :text="$t('navigation.systems.logs')"
            :button="{
                text: $t('systems.logs.reset'),
                emitEvent: 'resetRemoteAlarm',
                ability: remoteAlarms && remoteAlarms.length > 0,
            }"
            :searchField="{
                placeholder: $t('systems.logs.search'),
                inputEmitEvent: 'search',
                allowedFields: searchFields,
                defaultField: 'node_name',
            }"
            @search="searchHandler"
            @resetRemoteAlarm="showResetRemoteAlarmDialog"
        />

        <page-subnavigation :items="allSystemLinks" />

        <div class="table-wrapper">
            <table class="table table-filter table--striped table--filterable">
                <thead>
                    <tr>
                        <td width="10%">{{ $t('systems.logs.source_of_error') }}</td>
                        <td width="13%">{{ $t('fields.label') }}</td>
                        <!-- <td width="8%">{{ $t('systems.logs.number') }}</td> -->
                        <td width="41%">{{ $t('systems.logs.message') }}</td>
                        <td width="12%">{{ $t('systems.logs.date_and_time') }}</td>
                        <td width="10%">{{ $t('systems.logs.resolved_at') }}</td>
                        <td width="10%">{{ $t('systems.logs.notification') }}</td>
                        <!-- <td width="7%">{{ $t('systems.logs.fetched') }}</td> -->
                        <td width="4%">{{ $t('systems.logs.source') }}</td>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="log in filteredAlarms" :key="log.id">
                        <td>{{ log.node_name }}</td>
                        <td class="centered">
                            <template v-if="hasLabels(log)">
                                <pill-label v-for="(l, i) in log.labels" :key="'label_' + log.id + '_' + i" :left="l.label" :right="l.scope" :color="l.color" />
                            </template>
                            <alarm-message :alarm="log" :logs="true" />
                        </td>
                        <td>{{ log.error_message }}</td>
                        <td>{{ log.created_at_formatted }}</td>
                        <td>{{ log.resolved_at_formatted }}</td>

                        <td>
                            <span v-if="Object.keys(log.notifications).length <= 1">
                                <span v-html="log.notification_at_formatted.replace('<br>', ' ').slice(0, 19) + '<br>' + log.notification_at_formatted.replace('<br>', ' ').slice(20)"></span>
                                <i v-if="Object.keys(log.notifications).length > 0 && log.notifications[0].via_push" class="icon icon--push" style="margin-right:3px;"></i>
                                <i v-if="Object.keys(log.notifications).length > 0 && log.notifications[0].via_email" class="icon icon--mail" style="margin-right:3px;"></i>
                            </span>
                            <span v-else>
                                {{ formatDate(log.notifications[0].sent_at, 'Y-m-d H:i:s') }}<br />

                                <span v-if="log.notifications[0].user" class="smaller-text">({{ log.notifications[0].user.name }})</span>
                                <span v-else class="smaller-text">(N/A)</span>
                                &nbsp;

                                <i v-if="Object.keys(log.notifications).length > 0 && log.notifications[0].via_push" class="icon icon--push" style="margin-right:3px;"></i>
                                <i v-if="Object.keys(log.notifications).length > 0 && log.notifications[0].via_email" class="icon icon--mail" style="margin-right:3px;"></i>
                                <br />

                                <a href="#" @click="showMoreNotifications(log)" style="font-size:11px;text-decoration:underline;cursor:pointer;">{{ $t('systems.logs.more') }}</a>
                            </span>
                        </td>

                        <!-- <td class="positioned">
                            <span v-if="log.fetched === true" :title="$t('systems.logs.resynchronized')" class="trafficlight trafficlight--yellow">&nbsp;</span>
                            <span v-else-if="log.fetched === false" :title="$t('systems.logs.sent')" class="trafficlight trafficlight--green">&nbsp;</span>
                        </td> -->

                        <td class="positioned centered">
                            <i v-if="log.source === 'grafana'" class="icon icon--2x icon--grafana"></i>
                            <i v-if="log.source === 'system' || (!log.hasOwnProperty('source') && log.fetched === false)" class="icon icon--2x icon--common-system"></i>
                            <i
                                style="color:#E74342 !important;"
                                v-if="log.source === 'connect' || (!log.hasOwnProperty('source') && log.fetched === true)"
                                class="icon icon--2x icon--common-connect"
                            ></i>
                        </td>
                    </tr>
                </tbody>

                <TablePagination
                    v-if="paginationData"
                    :paginationData="paginationData"
                    :visibleAmount="searchedAlarms ? searchedAlarms.length : alarms.length"
                    @goToPrevPage="prevPageHandler"
                    @goToNextPage="nextPageHandler"
                />
            </table>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import TablePagination from '@/components/TablePagination';
import AlarmMessage from '@/components/AlarmMessage';
import PillLabel from '@/components/PillLabel';
import * as moment from 'moment';
import { mapState } from 'vuex';
import find from 'lodash/find';
import { clearTimeout } from 'timers';
import debounce from 'lodash/debounce';
import { createSearchParams } from '@/utils/searchParameters';
import formatDate from '@/mixins/formatDate';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    mixins: [formatDate, generalLinks],
    components: {
        PageHeader,
        TablePagination,
        AlarmMessage,
        PageSubnavigation,
        PillLabel,
    },
    data() {
        return {
            activeSearchTerm: false,
            alarms: [],
            searchedAlarms: false,
            remoteAlarms: [],
            searchFields: ['node_name', 'error_code', 'error_message'],
            paginationData: false,
            nonResettableAlarms: [71, 128, 133, 134, 144, 171, 320, 321, 322, 323, 324, 390, 520, 521, 522, 523, 524, 590],
        };
    },
    created() {
        this.loadAlarms();
        this.fetchRemoteAlarms();
    },
    computed: {
        ...mapState(['activeSystem', 'alarmCheckId']),
        filteredAlarms() {
            if (!this.alarms) return [];

            let filtered = this.searchedAlarms ? [...this.searchedAlarms] : [...this.alarms];
            return this.prepareAlarms(filtered);
        },
        activePage() {
            return this.paginationData ? this.paginationData.current_page : 0;
        },
    },
    watch: {
        $route: function() {
            this.loadAlarms();
            this.fetchRemoteAlarms();
        },
        activeSystem: {
            handler() {
                this.loadAlarms();
                this.fetchRemoteAlarms();
            },
        },
    },
    methods: {
        prevPageHandler() {
            if (this.activeSearchTerm) {
                this.searchAlarms(this.activeSearchTerm, this.activePage - 1);
            } else {
                this.loadAlarms(this.activePage - 1);
            }
        },
        nextPageHandler() {
            if (this.activeSearchTerm) {
                this.searchAlarms(this.activeSearchTerm, this.activePage + 1);
            } else {
                this.loadAlarms(this.activePage + 1);
            }
        },
        searchHandler: debounce(function(e) {
            if (e !== '') {
                this.activeSearchTerm = e;
                this.searchAlarms(e);
            } else {
                this.activeSearchTerm = false;
                this.searchedAlarms = false;
                this.loadAlarms();
            }
        }, 300),
        async loadAlarms(page = 1) {
            if (!this.activeSystem) return;
            try {
                const params = {
                    page: page,
                };
                const response = await api.systems.alarms(this.activeSystem.id, { params });
                if (response && response.data) {
                    this.alarms = response.data.alarms;
                    this.paginationData = response.data._metadata;
                }
            } catch (error) {
                console.error(error);
                this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
            }
        },
        async searchAlarms(searchTerm, page = 1) {
            const allowedParams = this.searchFields;
            const searchParams = createSearchParams(searchTerm, allowedParams, 'node_name');
            try {
                const params = {
                    ...searchParams,
                    page: page,
                };
                const response = await api.systems.alarmsSearch(this.activeSystem.id, { params });
                if (response && response.data) {
                    this.searchedAlarms = response.data.alarms;
                    this.paginationData = response.data._metadata;
                }
            } catch (e) {
                console.error(e);
            }
        },
        prepareAlarms(alarms) {
            return alarms.map(alarm => {
                let preparedAlarm = Object.assign({}, alarm);
                preparedAlarm.error_code_formatted = preparedAlarm.error_type + ' ' + preparedAlarm.error_code;
                preparedAlarm.created_at_formatted = moment(preparedAlarm.created_at).format('YYYY-MM-DD HH:mm:ss');
                preparedAlarm.resolved_at_formatted = moment(preparedAlarm.resolved_at).isValid() ? moment(preparedAlarm.resolved_at).format('YYYY-MM-DD HH:mm:ss') : ' ';
                //preparedAlarm.color = preparedAlarm.col moment(preparedAlarm.resolved_at).isValid() ? moment(preparedAlarm.resolved_at).format('YYYY-MM-DD HH:mm:ss') : ' ';

                let notification = '';
                if (preparedAlarm.notifications && preparedAlarm.notifications.length > 0) {
                    preparedAlarm.notifications.forEach(note => {
                        const sentFormatted = moment(note.sent_at).format('YYYY-MM-DD HH:mm:ss');
                        let s = sentFormatted;
                        if (note.user) {
                            s += ` <span class="smaller-text">(${note.user.name})</span>`;
                        } else {
                            s += ` <span class="smaller-text">(${note.email})</span>`;
                        }

                        notification += `${s}<br>`;
                    });
                }

                preparedAlarm.notification_at_formatted = notification;

                return preparedAlarm;
            });
        },
        fetchRemoteAlarms(checkForAlarmId = false) {
            if (!this.activeSystem) return;
            api.systems.remoteAlarms({ id: this.activeSystem.id }).then(response => {
                let allRemoteAlarms = response.data;

                if (checkForAlarmId && this.alarmCheckId) {
                    this.timeout = setTimeout(() => {
                        let alarmToCheck = find(allRemoteAlarms, { id: this.alarmCheckId });

                        if (!alarmToCheck || alarmToCheck.active === false) {
                            this.prepareRemoteAlarms(allRemoteAlarms);
                            clearTimeout(this.timeout);
                        } else {
                            this.fetchRemoteAlarms(true);
                        }
                    }, 20000);
                } else {
                    this.prepareRemoteAlarms(allRemoteAlarms);
                }
            });
        },
        prepareRemoteAlarms(allRemoteAlarms) {
            for (let r in allRemoteAlarms) {
                let alarm = allRemoteAlarms[r];
                alarm.resettable = this.nonResettableAlarms.indexOf(alarm.code) < 0;
                alarm.error_code = alarm.code;
                alarm.error_code_formatted = alarm.error_type + ' ' + this.formatErrorCode(alarm.code, 3);
                alarm.active_formatted = this.$t(`common.${alarm.active ? 'yes' : 'no'}`);
            }
            this.remoteAlarms = allRemoteAlarms;
        },
        showResetRemoteAlarmDialog() {
            this.$modal.show(
                () => import('@/views/modal/RemoteAlarms.vue'),
                { remoteAlarms: this.remoteAlarms, systemId: this.activeSystem.id },
                {
                    name: 'RemoteAlarms',
                    scrollable: true,
                    width: 850,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                },
                {
                    'before-close': () => {
                        this.fetchRemoteAlarms(true);
                    },
                }
            );
        },
        formatErrorCode(code, length) {
            return (1e4 + code + '').slice(-length);
        },
        showMoreNotifications(log) {
            this.$modal.show(
                () => import('@/views/modal/MoreNotifications.vue'),
                {
                    data: log,
                    successCallback: () => {
                        console.log('success');
                    },
                },
                {
                    name: 'MoreNotifications',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                    clickToClose: true,
                }
            );
        },
        hasLabels(log) {
            let retval = false;
            if (log.hasOwnProperty('labels') && log.labels.length > 0) {
                retval = true;
            }
            return retval;
        },
    },
};
</script>
